import React, { useState } from 'react';
import {
    styled,
    Grid,
    Box,
    Typography,
    CircularProgress,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Table, TableBody, TableRow, TableCell } from '@mui/material';
import { useTextInput, useToggle, useDatePicker } from 'hooks/forms';
import {
    Section,
    Header,
    Content,
} from 'components/ui/forms';
import { DeleteButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import { TextField, ProvinceSelector, DatePicker } from 'components/ui/inputs';
import { useNotification } from 'context/notification';
import { addPerson, updatePerson, deletePerson } from 'pages/stripeOnboarding/StripeOnboardingAPI';
import { ActionButton } from 'components/ui/buttons';
import ConfirmAction from 'components/popups/ConfirmAction';

const defaultModalState = {
    open: false,
    loading: false,
    person: null,
    mode: null, // create, edit, prefill
};

export default function Directors(props) {
    const { hostId, connectId, business, persons, representative, readOnly, loading, onUpdate } =
        props;

    const { businessType } = business;

    let directors = [];

    if (businessType === 'company') {
        directors = persons.filter((p) => p.director === true || p.owner === true);
    }
    if (businessType === 'non_profit') {
        directors = persons.filter((p) => p.director === true);
    }

    const [modal, setModal] = useState(defaultModalState);

    const { notify } = useNotification();

    const createDirectorModal = () => {
        setModal((m) => ({ ...m, open: true, mode: 'create' }));
    };

    const editDirectorModal = (director) => {
        setModal((m) => ({ ...m, open: true, person: director, mode: 'edit' }));
    };

    const prefillDirectorModal = () => {
        setModal((m) => ({ ...m, open: true, person: representative, mode: 'prefill' }));
    };

    const handleModalClose = () => setModal(defaultModalState);

    const handleModalSubmit = (personId, values) => {
        setModal((m) => ({ ...m, loading: true }));

        if (personId) {
            // Edit Person
            updatePerson(hostId, connectId, personId, values)
                .then((persons) => {
                    notify.success('Director updated');
                    handleModalClose();
                    onUpdate(persons);
                })
                .catch((error) => {
                    notify.error(error.message);
                    setModal((m) => ({ ...m, loading: false }));
                });
        } else {
            // Create Person
            addPerson(hostId, connectId, values)
                .then((persons) => {
                    notify.success('Director added');
                    handleModalClose();
                    onUpdate(persons);
                })
                .catch((error) => {
                    notify.error(error.message);
                    setModal((m) => ({ ...m, loading: false }));
                });
        }
    };

    const handleModalDelete = (personId) => {
        setModal((m) => ({ ...m, loading: true }));

        deletePerson(hostId, connectId, personId)
            .then((persons) => {
                notify.success('Director deleted');
                handleModalClose();
                onUpdate(persons);
            })
            .catch((error) => {
                notify.error(error.message);
                setModal((m) => ({ ...m, loading: false }));
            });
    };

    if (loading === true) {
        return (
            <Section>
                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={100}>
                    <CircularProgress />
                </Box>
            </Section>
        );
    }

    // *** Check if Representative could be added to Directors *** //

    let prefillRepresentative = false;

    if (representative && directors.every((d) => d.representative === false)) {
        prefillRepresentative = true;
    }

    return (
        <Section>
            <Box display={'flex'} justifyContent={'space-between'} mb={2}>
                {businessType === 'company' && <Header>Management and Ownership</Header>}
                {businessType === 'non_profit' && <Header>Board of Directors</Header>}

                <Box display={'flex'} justifyContent={'flex-end'}>
                    {prefillRepresentative && (
                        <Box mr={2}>
                            <ActionButton disabled={readOnly} onClick={prefillDirectorModal}>
                                + Add {representative.firstName} {representative.lastName}
                            </ActionButton>
                        </Box>
                    )}
                    <ActionButton disabled={readOnly} onClick={createDirectorModal}>
                        + Add
                    </ActionButton>
                </Box>
            </Box>
            <Box mb={2}>
                {businessType === 'company' && (
                    <Typography>
                        It's required to list any individual who is on the governing board, owns 25%
                        or more of the company or otherwise has significant management control of
                        the company. You can use your organization's general email address for each
                        individual if you prefer.
                    </Typography>
                )}
                {businessType === 'non_profit' && (
                    <Typography>
                        Only list members who are recognized as directors (in most cases, these are
                        the voting members) and appear on your official registry. You can use your
                        organization's general email address for each director if you prefer.
                    </Typography>
                )}
            </Box>
            <Content>
                {directors.length === 0 ? (
                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        height={100}
                    >
                        <Typography>You haven't added a directors yet</Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableBody>
                            {directors.map((director, index) => (
                                <TableRowClickable
                                    key={director.id}
                                    onClick={() => editDirectorModal(director)}
                                >
                                    <TableCell width={10}>{index + 1}.</TableCell>
                                    <TableCell>
                                        {director.firstName + ' ' + director.lastName}
                                    </TableCell>
                                    <TableCell>{director.email}</TableCell>
                                    <TableCell>
                                        <Roles
                                            businessType={businessType}
                                            representative={director.representative}
                                            director={director.director}
                                            owner={director.owner}
                                        />
                                    </TableCell>
                                </TableRowClickable>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Content>

            {modal.open && businessType === 'company' && (
                <PersonModal
                    {...modal}
                    readOnly={readOnly}
                    onClose={handleModalClose}
                    onSubmit={handleModalSubmit}
                    onDelete={handleModalDelete}
                />
            )}

            {modal.open && businessType === 'non_profit' && (
                <DirectorModal
                    {...modal}
                    readOnly={readOnly}
                    onClose={handleModalClose}
                    onSubmit={handleModalSubmit}
                    onDelete={handleModalDelete}
                />
            )}
        </Section>
    );
}

// PersonModal is for creating general Person records and assigning them roles

// Non-Profits use the DirectorModal instead

function PersonModal(props) {
    const { mode, person, readOnly, onClose, onSubmit, onDelete, loading } = props;

    const editingRepresentative = mode === 'edit' && person?.representative === true;

    const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);

    // Person Details
    const firstName = useTextInput({
        defaultValue: person?.firstName,
        disabled: readOnly || mode === 'prefill',
    });
    const lastName = useTextInput({
        defaultValue: person?.lastName,
        disabled: readOnly || mode === 'prefill',
    });
    const email = useTextInput({
        defaultValue: person?.email,
        disabled: readOnly || mode === 'prefill',
    });

    // Person Roles
    const director = useToggle({ defaultValue: person?.director, disabled: readOnly });
    const owner = useToggle({ defaultValue: person?.owner, disabled: readOnly });

    // Person DOB + Address (for owners only)
    const dob = useDatePicker({
        defaultValue: person?.dob,
        disabled: readOnly || mode === 'prefill',
    });
    const city = useTextInput({
        defaultValue: person?.city,
        disabled: readOnly || mode === 'prefill',
    });
    const address = useTextInput({
        defaultValue: person?.address,
        disabled: readOnly || mode === 'prefill',
    });
    const unit = useTextInput({
        defaultValue: person?.unit,
        disabled: readOnly || mode === 'prefill',
    });
    const postal = useTextInput({
        defaultValue: person?.postal,
        disabled: readOnly || mode === 'prefill',
    });
    const province = useTextInput({
        defaultValue: person?.province,
        disabled: readOnly || mode === 'prefill',
    });

    // **** Check if form is valid ***** //

    let disableSubmit = false;
    let disableRemove = false;
    let disableCancel = false;

    if (loading) {
        disableSubmit = true;
        disableRemove = true;
        disableCancel = true;
    }

    if (readOnly) {
        disableSubmit = true;
        disableRemove = true;
    }

    if (owner.checked === false && director.checked === false) {
        disableSubmit = true;
    }

    if (mode === 'edit') {
        // In edit mode, only enable form if there are changes to existing values
        if (
            person.firstName === firstName.value &&
            person.lastName === lastName.value &&
            person.email === email.value &&
            person.director === director.checked &&
            person.owner === owner.checked &&
            person.dob === dob.value &&
            person.city === city.value &&
            person.address === address.value &&
            person.unit === unit.value &&
            person.postal === postal.value &&
            person.province === province.value
        ) {
            disableSubmit = true;
        }
    }

    const { notify } = useNotification();

    const handleDelete = () => {
        if (editingRepresentative) {
            // Remove director role from the account representative
            onSubmit(person.id, { director: false, owner: false });
        } else {
            // Delete this Person
            onDelete(person.id);
        }
        setConfirmDeleteDialog(false);
    };

    const handleSubmit = () => {
        let formError = false;

        [firstName, lastName, email].forEach((field) => {
            if (field.value.trim().length === 0) {
                field.setError('This field cannot be empty');
                formError = true;
            }
        });

        if (owner.checked === true) {
            [city, address, postal, province].forEach((field) => {
                if (field.value.trim().length === 0) {
                    field.setError('This field cannot be empty');
                    formError = true;
                }
            });
            if (dob.value === null || dob.value.isValid() === false) {
                dob.setError('This field cannot be empty');
                formError = true;
            }
        }

        if (owner.checked === false && director.checked === false) {
            formError = true;
        }

        if (formError) {
            notify.error('Please fill out all fields');
            return;
        }

        // Update Representative with new roles
        if (mode === 'prefill') {
            onSubmit(person.id, {
                owner: owner.checked,
                director: director.checked,
            });

            return;
        }

        let personId = null;

        // Add a new Person
        if (mode === 'create') {
            personId = null;
        }

        // Edit existing Director
        if (mode === 'edit') {
            personId = person?.id;
        }

        let formValues = {
            firstName: firstName.value.trim(),
            lastName: lastName.value.trim(),
            email: email.value.trim(),
            owner: owner.checked,
            director: director.checked,
        };

        if (owner.checked === true) {
            formValues = {
                ...formValues,
                dob: dob.value.format('YYYY-MM-DD'),
                city: city.value.trim(),
                address: address.value.trim(),
                unit: unit.value.trim(),
                postal: postal.value.trim(),
                province: province.value.trim(),
            };
        }

        onSubmit(personId, formValues);
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={true} onClose={onClose}>
            <DialogTitle>
                {mode === 'prefill' && 'Add an Individual'}
                {mode === 'create' && 'Add an Individual'}
                {mode === 'edit' && 'Edit Individual'}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            autoFocus={mode === 'create'}
                            label="First Name"
                            {...firstName.formProps}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="Last Name" {...lastName.formProps} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Email" {...email.formProps} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box mt={1} mb={1}>
                            <Typography>Pick one or both of following:</Typography>
                        </Box>
                        <FormControlLabel
                            control={<Checkbox {...owner.formProps} />}
                            label={'I own 25% or more of the company'}
                        />
                        <FormControlLabel
                            control={<Checkbox {...director.formProps} />}
                            label={
                                "I'm a director of the company, or a member of the governing board"
                            }
                        />
                    </Grid>
                </Grid>

                {owner.checked === true && (
                    <Box mt={4}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <DatePicker label="Date of Birth" {...dob.formProps} />
                            </Grid>
                            <Grid item xs={9} md={8}>
                                <TextField label="Home Address" {...address.formProps} />
                            </Grid>
                            <Grid item xs={3} md={4}>
                                <TextField label="Unit #" {...unit.formProps} />
                            </Grid>
                            <Grid item xs={6} sm={6} md={4}>
                                <TextField label="City" {...city.formProps} />
                            </Grid>
                            <Grid item xs={6} sm={6} md={4}>
                                <ProvinceSelector region="CA" {...province.formProps} />
                            </Grid>
                            <Grid item xs={6} sm={6} md={4}>
                                <TextField label="Postal Code" {...postal.formProps} />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {mode === 'edit' && (
                    <DeleteButton
                        sx={{ marginRight: 'auto' }}
                        onClick={() => setConfirmDeleteDialog(true)}
                        disabled={disableRemove}
                    >
                        Remove
                    </DeleteButton>
                )}
                <CancelButton onClick={onClose} disabled={disableCancel}>
                    Cancel
                </CancelButton>
                <SubmitButton onClick={handleSubmit} loading={loading} disabled={disableSubmit}>
                    {mode === 'edit' ? 'Update' : 'Add'}
                </SubmitButton>
            </DialogActions>

            {mode === 'edit' && (
                <ConfirmAction
                    open={confirmDeleteDialog}
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmDeleteDialog(false)}
                    title={'Are you sure?'}
                    description={
                        editingRepresentative
                            ? `${person.firstName} ${person.lastName} will be removed from Management and Ownership, but will remain the account representative.`
                            : `${person.firstName} ${person.lastName} will be removed from Management and Ownership.`
                    }
                    destructive={true}
                    confirmText={'Remove'}
                />
            )}
        </Dialog>
    );
}

// DirectorModal is just for creating Person records with the director role

function DirectorModal(props) {
    const { mode, person, readOnly, onClose, onSubmit, onDelete, loading } = props;
    const director = person;

    const editingRepresentative = mode === 'edit' && director?.representative === true;

    const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);

    const firstName = useTextInput({
        defaultValue: director?.firstName,
        disabled: mode === 'prefill' || readOnly,
    });
    const lastName = useTextInput({
        defaultValue: director?.lastName,
        disabled: mode === 'prefill' || readOnly,
    });
    const email = useTextInput({
        defaultValue: director?.email,
        disabled: mode === 'prefill' || readOnly,
    });

    // **** Check if form is valid ***** //

    let unsavedChanges = true;

    if (mode === 'edit') {
        // In edit mode, only enable form if there are changes to existing values
        if (
            director.firstName === firstName.value &&
            director.lastName === lastName.value &&
            director.email === email.value
        ) {
            unsavedChanges = false;
        }
    }

    const { notify } = useNotification();

    const handleDelete = () => {
        if (editingRepresentative) {
            // Remove director role from the account representative
            onSubmit(director.id, { director: false });
        } else {
            // Delete this Person
            onDelete(director.id);
        }
        setConfirmDeleteDialog(false);
    };

    const handleSubmit = () => {
        let formError = false;

        [firstName, lastName, email].forEach((field) => {
            if (field.value.trim().length === 0) {
                field.setError('This field cannot be empty');
                formError = true;
            }
        });

        if (formError) {
            notify.error('Please fill out all fields');
            return;
        }

        // Update Representative to be Director
        if (mode === 'prefill') {
            onSubmit(director.id, { director: true });
        }

        // Add a new Director
        if (mode === 'create') {
            onSubmit(null, {
                firstName: firstName.value.trim(),
                lastName: lastName.value.trim(),
                email: email.value.trim(),
                director: true,
            });
        }

        // Edit existing Director
        if (mode === 'edit') {
            onSubmit(director?.id, {
                firstName: firstName.value.trim(),
                lastName: lastName.value.trim(),
                email: email.value.trim(),
            });
        }
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={true} onClose={onClose}>
            <DialogTitle>
                {mode === 'prefill' && 'Add a Director'}
                {mode === 'create' && 'Add a Director'}
                {mode === 'edit' && 'Edit Director'}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            autoFocus={mode === 'create'}
                            label="First Name"
                            {...firstName.formProps}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="Last Name" {...lastName.formProps} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Email" {...email.formProps} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                {mode === 'edit' && (
                    <DeleteButton
                        sx={{ marginRight: 'auto' }}
                        onClick={() => setConfirmDeleteDialog(true)}
                        disabled={readOnly || loading}
                    >
                        Remove
                    </DeleteButton>
                )}
                <CancelButton onClick={onClose} disabled={loading}>
                    Cancel
                </CancelButton>
                <SubmitButton
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={readOnly || loading || unsavedChanges === false}
                >
                    {mode === 'edit' ? 'Update' : 'Add'}
                </SubmitButton>
            </DialogActions>

            {mode === 'edit' && (
                <ConfirmAction
                    open={confirmDeleteDialog}
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmDeleteDialog(false)}
                    title={'Are you sure?'}
                    description={
                        editingRepresentative
                            ? `${director.firstName} ${director.lastName} will be removed from the board of directors, but will remain the account representative.`
                            : `${director.firstName} ${director.lastName} will be removed from the board of directors.`
                    }
                    destructive={true}
                    confirmText={'Remove'}
                />
            )}
        </Dialog>
    );
}

function Roles(props) {
    const { businessType, representative, director, owner } = props;

    let roles = [];

    if (representative) roles.push('Account Rep');
    if (director) roles.push('Director');

    if (businessType === 'company') {
        if (owner) roles.push('Owner');
    }

    return <Typography>{roles.join(', ')}</Typography>;
}

const TableRowClickable = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
    },
}));
